import { BookOpenIcon, UploadIcon } from "@ui/components/Icon";
import { accentSegment } from "@ui/lib/accent-classes";
import { tv } from "@ui/lib/tv";
import { Button } from "@ui/primitives/Button";
import { DropZone, FileTrigger, useSingleFileDrop } from "@ui/primitives/DropZone";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import type { ReactNode } from "react";

const fileDropField = tv({
  slots: {
    root: "w-full rounded-2xl",
    dropCard: [
      "flex w-full cursor-pointer flex-col items-center justify-center gap-2",
      "rounded-2xl border-2 border-neutral-300 border-dashed bg-white px-6 py-10 text-center",
      "transition-colors hover:border-neutral-400 hover:bg-neutral-25",
    ],
    dropIcon: "flex size-11 items-center justify-center rounded-full bg-green-100 text-green-700 [&_svg]:size-5",
    dropTitle: "font-semibold text-neutral-800",
    dropHint: "text-neutral-500 text-sm",
    fileCard: "flex w-full items-center gap-3 rounded-2xl border border-green-500 bg-green-50 px-4 py-3",
    fileIcon: "flex size-10 shrink-0 items-center justify-center rounded-lg bg-white text-green-700 [&_svg]:size-5",
    fileExt: "font-semibold text-[11px] text-green-700 uppercase tracking-wide",
    fileBody: "flex min-w-0 flex-1 flex-col text-left",
    fileName: "truncate font-medium text-green-900",
    fileMeta: "truncate text-green-800/60 text-sm",
    actions: "flex shrink-0 items-center gap-1",
    replaceButton:
      "cursor-pointer rounded-full pressed:bg-green-200 px-3 py-1.5 font-medium text-green-800 text-sm transition-colors hover:bg-green-100",
    removeButton:
      "cursor-pointer rounded-full pressed:bg-green-200 px-3 py-1.5 font-medium text-green-700/70 text-sm transition-colors hover:bg-green-100 hover:text-green-900",
    progressTrack: "mt-3 h-1.5 w-full overflow-hidden rounded-full bg-neutral-200",
    progressBar: "h-full rounded-full transition-all duration-150",
    message: "mt-2 text-base text-pink-600",
  },
  variants: {
    color: {
      red: { progressBar: accentSegment.red },
      green: { progressBar: accentSegment.green },
      purple: { progressBar: accentSegment.purple },
    },
    isDropTarget: {
      true: { root: "ring-2 ring-offset-2" },
    },
  },
  compoundVariants: [
    { isDropTarget: true, color: "red", class: { root: "ring-red-400" } },
    { isDropTarget: true, color: "green", class: { root: "ring-green-500" } },
    { isDropTarget: true, color: "purple", class: { root: "ring-purple-500" } },
  ],
  defaultVariants: { color: "green" },
});

export interface FileDropFieldProps {
  /** Accessible name for the drop zone and its trigger. */
  "aria-label": string;
  /** MIME types to accept, e.g. `["application/pdf"]`. Omit to accept any file. */
  acceptedFileTypes?: string[];
  /** Headline inside the empty drop card, e.g. "Drag a file here, or browse". */
  title: string;
  /** Secondary line, e.g. "PDF, ZIP, video — up to 250 MB". */
  hint?: string;
  /** Name of the attached/selected file. Its presence switches to the file-card state. */
  fileName?: string;
  /** Size of the selected file in bytes — shown when known (typically only fresh uploads). */
  fileSize?: number;
  onFileSelect: (file: File) => void;
  onRemove?: () => void;
  isUploading?: boolean;
  /** Upload progress 0–100; renders a progress bar while `isUploading`. */
  progress?: number;
  color?: "red" | "green" | "purple";
  /** Label for the replace control on the file card. */
  replaceLabel: string;
  /** Label for the clear/remove control on the file card (rendered beside Replace). */
  removeLabel: string;
  errorMessage?: ReactNode;
  className?: string;
}

/**
 * A styled single-file upload field for any file type. Empty state is a drag-and-drop card
 * (click to browse); once a file is attached it shows a file card with replace / remove
 * controls and an optional upload progress bar. Built on the `DropZone` + `FileTrigger`
 * primitives. The companion `StandardDropZone` is image-only; reach for this for documents,
 * archives, video, etc.
 */
export function FileDropField({
  "aria-label": ariaLabel,
  acceptedFileTypes,
  title,
  hint,
  fileName,
  fileSize,
  onFileSelect,
  onRemove,
  isUploading,
  progress = 0,
  color = "green",
  replaceLabel,
  removeLabel,
  errorMessage,
  className,
}: FileDropFieldProps) {
  const styles = fileDropField({ color });
  const { onDrop, onSelect } = useSingleFileDrop(onFileSelect);
  const meta = fileSize != null ? formatBytes(fileSize) : null;
  const ext = fileName ? fileExtension(fileName) : null;
  const reducedMotion = useReducedMotion() ?? false;
  const fade = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: reducedMotion ? 0 : 0.18 },
  };

  return (
    <div className={className}>
      {/* `layout` eases the height delta as the drop card (empty) gives way to the shorter file card,
          while the crossfade swaps their contents — so attaching or removing a file feels continuous
          rather than a hard pop. No overflow clip here: the drag-target ring needs to paint past the edge. */}
      <motion.div
        className="relative"
        layout={reducedMotion ? false : "size"}
        transition={{ duration: reducedMotion ? 0 : 0.28, ease: "easeOut" }}
      >
        <DropZone
          aria-label={ariaLabel}
          className={({ isDropTarget }) => fileDropField({ color, isDropTarget }).root()}
          onDrop={onDrop}
        >
          <AnimatePresence initial={false} mode="popLayout">
            {fileName ? (
              <motion.div key="file" className={styles.fileCard()} {...fade}>
                <span aria-hidden="true" className={styles.fileIcon()}>
                  {ext ? <span className={styles.fileExt()}>{ext}</span> : <BookOpenIcon />}
                </span>
                <span className={styles.fileBody()}>
                  <span className={styles.fileName()}>{fileName}</span>
                  {meta ? <span className={styles.fileMeta()}>{meta}</span> : null}
                </span>
                <span className={styles.actions()}>
                  <FileTrigger acceptedFileTypes={acceptedFileTypes} onSelect={onSelect}>
                    <Button className={styles.replaceButton()}>{replaceLabel}</Button>
                  </FileTrigger>
                  {onRemove ? (
                    <Button className={styles.removeButton()} onPress={onRemove}>
                      {removeLabel}
                    </Button>
                  ) : null}
                </span>
              </motion.div>
            ) : (
              <motion.div key="empty" {...fade}>
                <FileTrigger acceptedFileTypes={acceptedFileTypes} onSelect={onSelect}>
                  {/* No aria-label here: the DropZone already carries `ariaLabel` as the drop region's
                      name, so the browse trigger takes its accessible name from its visible title/hint
                      instead of duplicating it (two buttons sharing one name is an a11y smell). */}
                  <Button className={styles.dropCard()}>
                    <span aria-hidden="true" className={styles.dropIcon()}>
                      <UploadIcon />
                    </span>
                    <span className={styles.dropTitle()}>{title}</span>
                    {hint ? <span className={styles.dropHint()}>{hint}</span> : null}
                  </Button>
                </FileTrigger>
              </motion.div>
            )}
          </AnimatePresence>
        </DropZone>
      </motion.div>

      {isUploading ? (
        <div aria-hidden="true" className={styles.progressTrack()}>
          <div className={styles.progressBar()} style={{ width: `${progress}%` }} />
        </div>
      ) : null}

      {errorMessage ? <p className={styles.message()}>{errorMessage}</p> : null}
    </div>
  );
}

/** Short uppercase extension for the type chip (e.g. "PDF"), or null when there isn't a tidy one. */
function fileExtension(name: string): string | null {
  const dot = name.lastIndexOf(".");
  if (dot < 1 || dot === name.length - 1) return null;
  const ext = name.slice(dot + 1);
  return /^[a-z0-9]{1,4}$/i.test(ext) ? ext.toUpperCase() : null;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 || Number.isInteger(value) ? 0 : 1)} ${units[unit]}`;
}
